import React from 'react';
import { Clock, User, MapPin, Monitor } from 'lucide-react';
import type { AuditLogEntry } from '../../../core/models/SignatureEnvelope';
import { format } from 'date-fns';
import { Timestamp } from 'firebase/firestore';

interface AuditTimelineProps {
  auditLog: AuditLogEntry[];
}

export const AuditTimeline: React.FC<AuditTimelineProps> = ({ auditLog }) => {
  if (!auditLog || auditLog.length === 0) {
    return (
      <div className="text-center py-8 text-gray-500">
        <p>No audit entries yet</p>
      </div>
    );
  }

  const getActionLabel = (action: string) => {
    return action
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  const getActorLabel = (entry: AuditLogEntry) => {
    if (entry.actorRole === 'client') {
      return 'Client';
    }
    return 'Company';
  };

  return (
    <div className="space-y-4">
      {auditLog.map((entry, index) => {
        // Handle Firestore Timestamp or Date
        let timestamp: Date;
        const rawTimestamp: any = (entry as any).timestamp;
        if (rawTimestamp instanceof Date) {
          timestamp = rawTimestamp;
        } else if (rawTimestamp instanceof Timestamp) {
          timestamp = rawTimestamp.toDate();
        } else if (rawTimestamp && typeof rawTimestamp.toDate === 'function') {
          timestamp = rawTimestamp.toDate();
        } else {
          timestamp = new Date();
        }

        return (
          <div key={index} className="flex gap-4 relative">
            {/* Timeline line */}
            {index < auditLog.length - 1 && (
              <div className="absolute left-5 top-12 bottom-0 w-0.5 bg-gray-200" />
            )}

            {/* Icon */}
            <div className="relative z-10 flex-shrink-0">
              <div className="flex items-center justify-center w-10 h-10 rounded-full bg-primary/10 border-2 border-primary">
                <Clock className="h-5 w-5 text-primary" />
              </div>
            </div>

            {/* Content */}
            <div className="flex-1 pb-6">
              <div className="bg-white border rounded-lg p-4 shadow-sm">
                <div className="flex items-start justify-between mb-2">
                  <div>
                    <div className="font-medium text-gray-900">
                      {getActionLabel(entry.action)}
                    </div>
                    <div className="text-sm text-gray-600 mt-1">
                      <div className="flex items-center gap-2">
                        <User className="h-4 w-4" />
                        {getActorLabel(entry)} - {entry.actor}
                      </div>
                    </div>
                  </div>
                  <div className="text-xs text-gray-500">
                    {format(timestamp, 'MMM d, yyyy h:mm a')}
                  </div>
                </div>

                {/* Metadata */}
                <div className="mt-3 space-y-1 text-xs text-gray-500">
                  {entry.ipAddress && (
                    <div className="flex items-center gap-2">
                      <MapPin className="h-3 w-3" />
                      IP: {entry.ipAddress}
                    </div>
                  )}
                  {entry.userAgent && (
                    <div className="flex items-center gap-2">
                      <Monitor className="h-3 w-3" />
                      {entry.userAgent.length > 50
                        ? `${entry.userAgent.substring(0, 50)}...`
                        : entry.userAgent}
                    </div>
                  )}
                  {entry.metadata && Object.keys(entry.metadata).length > 0 && (
                    <div className="mt-2 pt-2 border-t">
                      {Object.entries(entry.metadata).map(([key, value]) => (
                        <div key={key} className="text-xs">
                          <span className="font-medium">{key}:</span> {String(value)}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};
